import { useAuthStore } from '@/store/authStore';
import { createPortal } from 'react-dom';
import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import HomeTagFilter from '@/components/dashboard/HomeTagFilter';
import { useLinkedPlantTags } from '@/hooks/useLinkedPlantTags';
import { usePageTitle } from '@/layouts/usePageTitle';
import { fetchDetailCards } from '@/api/powerplantDetail';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz, colorSchemeLightCold } from 'ag-grid-community';
import { LayoutGrid, PanelsTopLeft, Search, Table2, Zap } from 'lucide-react';
import ColumnValuesFilter from '@/components/common/ColumnValuesFilter';
import EntityLink from '@/components/common/EntityLink';
import type { ColDef } from 'ag-grid-community';
import { fetchInverterHeatmap, fetchInverters, type Inverter, type InverterHeatmapRow } from '@/api/inverters';
import InverterHeatmapPanel from '@/components/inverters/InverterHeatmapPanel';
import { useI18n } from '@/i18n';
import toast from 'react-hot-toast';
import { useErrorToast } from '@/lib/notify';

function readCardView(key:string):boolean {
  try {return localStorage.getItem(key)==='cards';}catch{return false;}
}

function metricNumber(value:unknown):number|null {
  if(value==null || value==='')return null;
  const number=Number(value);
  return Number.isFinite(number)?number:null;
}
function formatMetric(value:unknown,unit:string):string {
  const number=metricNumber(value);
  return number===null?'—':`${number.toLocaleString('tr-TR',{maximumFractionDigits:2})} ${unit}`;
}

function formatEnergy(value:unknown):string {
  const kwh=metricNumber(value);
  if(kwh===null)return '—';
  const divisor=Math.abs(kwh)>=1_000_000?1_000_000:Math.abs(kwh)>=1000?1000:1;
  return formatMetric(kwh/divisor,divisor===1_000_000?'GWh':divisor===1000?'MWh':'kWh');
}

const modules = [AllEnterpriseModule];

const theme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 11.5,
  rowHeight: 32,
  headerHeight: 32,
  borderRadius: 6,
  headerBackgroundColor: '#f8fafc',
  borderColor: '#e2e8f0',
  oddRowBackgroundColor: '#f8fafc',
});

function StatusBadge({ value }: { value: number }) {
  const { t } = useI18n();
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      value === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
    }`}>
      {value === 1 ? t('common.online') : t('common.offline')}
    </span>
  );
}

const lastSeenFormatters = new Map<string, Intl.DateTimeFormat>();
function formatLastSeen(value: unknown, timeZone = 'Europe/Istanbul') {
  if (!value) return '—';
  const date = new Date(String(value));
  if (!Number.isFinite(date.getTime())) return '—';
  let formatter = lastSeenFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('tr-TR', { dateStyle: 'short', timeStyle: 'medium', timeZone });
    lastSeenFormatters.set(timeZone, formatter);
  }
  return formatter.format(date);
}

export default function InvertersPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const headerActionsTarget = useHeaderActionsTarget();
  const userId=useAuthStore(state=>state.user?.id);
  const viewStorageKey=`inverters:view:${userId ?? 'guest'}`;
  const [cardView,setCardView]=useState(()=>readCardView(viewStorageKey));
  useEffect(()=>{setCardView(readCardView(viewStorageKey));},[viewStorageKey]);
  const toggleView=()=>{
    const next=!cardView;
    setCardView(next);
    try{localStorage.setItem(viewStorageKey,next?'cards':'table');}catch{/* Keep the current view if browser storage is unavailable. */}
  };
  const [searchParams] = useSearchParams();
  const { uuid_id } = useParams<{ uuid_id?: string }>();

  // plant uuid from either route param or query param
  const plantUuid = uuid_id ?? searchParams.get('plant') ?? undefined;
  const [plantTitle, setPlantTitle] = useState<{ uuid: string; name: string } | null>(null);
  usePageTitle(plantUuid && plantTitle?.uuid === plantUuid ? `${plantTitle.name} - İnverterlar` : t('layout.nav.inverters'));

  useEffect(() => {
    if (!plantUuid) return;
    let cancelled = false;
    fetchDetailCards(plantUuid).then(cards => {
      if (!cancelled && cards.plantName) setPlantTitle({ uuid: plantUuid, name: cards.plantName });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [plantUuid]);


  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'offline' | 'warning'>('all');
  const [rowData, setRowData] = useState<Inverter[]>([]);
  const {availableTags,selectedTagIds,setSelectedTagIds,tagFilteredRows}=useLinkedPlantTags(rowData);
  const [loading, setLoading] = useState(true);
  const [heatmapLoading, setHeatmapLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);
  const [heatmapRows, setHeatmapRows] = useState<InverterHeatmapRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setHeatmapLoading(true);
    setError('');

    fetchInverters(plantUuid)
      .then((data) => {
        if (cancelled) return;
        setRowData(data);
        if (plantUuid && data[0]?.plant_name) setPlantTitle({ uuid: plantUuid, name: data[0].plant_name });

      })
      .catch(() => { if (!cancelled) setError(t('inverters.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });

    fetchInverterHeatmap(plantUuid)
      .then((data) => {
        if (cancelled) return;
        setHeatmapRows(data.rows || []);
      })
      .catch((error) => {
        if (cancelled) return;
        setHeatmapRows([]);
        const message = (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.error
          || (error as { response?: { data?: { error?: string; message?: string } } })?.response?.data?.message
          || 'Heatmap verisi alinamadi';
        toast.error(message);
      })
      .finally(() => { if (!cancelled) setHeatmapLoading(false); });
    return () => { cancelled = true; };
  }, [plantUuid, t]);

  const warningIds=useMemo(()=>new Set(heatmapRows.filter(row=>{
    const ratio=metricNumber(row.performance_ratio);
    return row.status===1 && ratio!==null && ratio<60;
  }).map(row=>Number(row.inverter_id))),[heatmapRows]);

  const filteredRows = useMemo(() => tagFilteredRows.filter(row =>
    statusFilter === 'all' || (statusFilter === 'warning' ? warningIds.has(Number(row.id)) : (row.status === 1) === (statusFilter === 'online')),
  ), [tagFilteredRows, statusFilter, warningIds]);
  const onlineCount = tagFilteredRows.filter(row => row.status === 1).length;
  const statusOptions = [
    { value: 'all', label: 'Tümü', count: tagFilteredRows.length },
    { value: 'online', label: t('common.online'), count: onlineCount },
    { value: 'offline', label: t('common.offline'), count: tagFilteredRows.length - onlineCount },
    { value: 'warning', label: 'Uyarı', count: tagFilteredRows.filter(row=>warningIds.has(Number(row.id))).length },
  ] as const;

  const heatmapData = useMemo<InverterHeatmapRow[]>(() => {
    if (heatmapRows.length > 0) {
      const dailyById=new Map(tagFilteredRows.map(row=>[Number(row.id),row.energy_day_kwh]));
      const current=heatmapRows.map(row=>({...row,daily_energy_kwh:metricNumber(row.daily_energy_kwh ?? dailyById.get(Number(row.inverter_id)))}));
      if(!selectedTagIds.length)return current;
      const ids=new Set(tagFilteredRows.map(row=>Number(row.id)));
      return current.filter(row=>ids.has(Number(row.inverter_id)));
    }

    return tagFilteredRows.map((row) => ({
      inverter_id: row.id,
      inverter_uuid_id: '',
      inverter_name: row.inverter_name,
      inverter_capacity_kwp: null,
      plant_id: 0,
      plant_uuid: row.plant_uuid,
      plant_name: row.plant_name,
      status: row.status,
      power_kw: null,
      daily_energy_kwh: metricNumber(row.energy_day_kwh),
      performance_ratio: null,
      internal_temperature: null,
      plant_timezone: row.plant_timezone,
      last_seen: row.last_seen,
    }));
  }, [heatmapRows, tagFilteredRows, selectedTagIds]);

  const gridRows=useMemo(()=>{
    const metrics=new Map(heatmapRows.map(row=>[Number(row.inverter_id),row]));
    return filteredRows.map(row=>{
      const latest=metrics.get(Number(row.id));
      return {...row,energy_day_kwh:metricNumber(latest?.daily_energy_kwh ?? row.energy_day_kwh),power_kw:metricNumber(latest?.power_kw),internal_temperature:metricNumber(latest?.internal_temperature),performance_ratio:metricNumber(latest?.performance_ratio)};
    });
  },[filteredRows,heatmapRows]);

  const visibleHeatmapRows=useMemo(()=>{
    const words=search.trim().toLocaleLowerCase('tr').split(/\s+/).filter(Boolean);
    const ids=new Set(gridRows.filter(row=>{
      const text=[row.inverter_name,row.brand,row.model,row.investor_name,row.plant_name,row.e_day,row.e_total,row.last_seen,formatLastSeen(row.last_seen,row.plant_timezone),row.power_kw,row.internal_temperature,row.performance_ratio,
        row.status===1?t('common.online'):t('common.offline')].join(' ').toLocaleLowerCase('tr');
      return words.every(word=>text.includes(word));
    }).map(row=>row.id));
    return heatmapData.filter(row=>ids.has(row.inverter_id));
  },[gridRows,heatmapData,search,t]);

  const dailySummary=useMemo(()=>{
    const values=visibleHeatmapRows.map(row=>metricNumber(row.daily_energy_kwh)).filter((value):value is number=>value!==null);
    const powers=visibleHeatmapRows.map(row=>metricNumber(row.power_kw)).filter((value):value is number=>value!==null);
    const power=powers.length?powers.reduce((sum,value)=>sum+value,0):visibleHeatmapRows.some(row=>row.status===1)?null:0;
    return {power,value:values.length?values.reduce((sum,value)=>sum+value,0):visibleHeatmapRows.length?null:0,count:values.length,total:visibleHeatmapRows.length};
  },[visibleHeatmapRows]);

  const colDefs = useMemo<ColDef<Inverter>[]>(() => [
    {
      field: 'inverter_name',
      headerName: t('inverters.inverter'),
      flex: 2,
      minWidth: 220,
      filter: ColumnValuesFilter,
      pinned: 'left',
      cellRenderer: ({data,value}:{data?:Inverter;value?:string})=><div className="flex h-full min-w-0 items-center gap-2">
        {data?.id ? <Link to={`/dashboard/inverters/${data.id}`} className="min-w-0 truncate font-semibold text-blue-600 hover:text-blue-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" title={value}>{value || '—'}</Link> : <span className="min-w-0 truncate">{value || '—'}</span>}
        {data && <span className="ml-auto inline-flex shrink-0 items-center gap-1">
          {([{label:'F',name:'FTP',active:data.ftp_connection_status===1},{label:'M',name:'Modbus',active:data.modbus_connection_status===1}]).map(connection=><span key={connection.label} aria-label={`${connection.name}: ${connection.active?'Güncel veri var':'Güncel veri yok'}`} title={`${connection.name}: ${connection.active?'Son 31 dakika içinde veri alındı':'Son 31 dakika içinde veri alınmadı'}`} className={`inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold ${connection.active?'bg-emerald-100 text-emerald-700':'bg-rose-100 text-rose-700'}`}>{connection.label}</span>)}
        </span>}
        {data?.has_panel_configuration && <span title="Panel tanımlı" aria-label="Panel tanımlı" className="shrink-0 rounded bg-blue-50 p-1 text-blue-600"><PanelsTopLeft size={14}/></span>}
      </div>,
    },
    {
      field: 'brand',
      headerName: t('inverters.brand'),
      flex: 1,
      minWidth: 120,
      filter: ColumnValuesFilter,
    },
    {
      field: 'model',
      headerName: t('inverters.model'),
      flex: 1,
      minWidth: 120,
      filter: ColumnValuesFilter,
    },
    {
      field: 'investor_name',
      headerName: t('common.investor'),
      flex: 2,
      minWidth: 150,
      filter: ColumnValuesFilter,
      cellRenderer: ({ data, value }: { data?: Inverter; value?: string }) => (
        <EntityLink to={data?.investor_uuid ? `/dashboard/investor/${data.investor_uuid}` : null}>{value}</EntityLink>
      ),
    },
    ...(!plantUuid ? [{
      field: 'plant_name' as keyof Inverter,
      headerName: t('inverters.plant'),
      flex: 2,
      minWidth: 160,
      filter: ColumnValuesFilter,
      cellRenderer: ({ data, value }: { data?: Inverter; value?: string }) => (
        <EntityLink to={data?.plant_uuid ? `/dashboard/powerplants/${data.plant_uuid}` : null}>{value}</EntityLink>
      ),
    } satisfies ColDef<Inverter>] : []),
    {
      field: 'status',
      headerName: t('common.status'),
      width: 110,
      filter: ColumnValuesFilter,
      filterValueGetter: params => params.data?.status === 1 ? t('common.online') : t('common.offline'),
      getQuickFilterText: params => params.value === 1 ? t('common.online') : t('common.offline'),
      cellRenderer: ({ value }: { value: number }) => <StatusBadge value={value} />,
    },
    {
      field:'power_kw',headerName:'Anlık Üretim (kW)',width:155,minWidth:140,
      type:'numericColumn',cellDataType:'number',filter:'agNumberColumnFilter',
      cellClass:'ag-right-aligned-cell tabular-nums',
      valueFormatter:({value})=>formatMetric(value,'kW'),
      headerTooltip:'Son güncel inverter güç ölçümü. Eski veya eksik ölçümler gösterilmez.',
    },
    ...([
      ['energy_day_kwh','Günlük Üretim','Bugünün toplam üretimi.'],
      ['energy_week_kwh','Haftalık Üretim','Bugün dahil son 7 günün üretimi; santral saat dilimi kullanılır.'],
      ['energy_month_kwh','Aylık Üretim','İçinde bulunulan takvim ayının toplam üretimi.'],
      ['energy_year_kwh','Yıllık Üretim','İçinde bulunulan takvim yılının toplam üretimi.'],
      ['energy_total_kwh','Toplam Üretim','İnverterin toplam enerji sayacı.'],
    ] as const).map(([field,headerName,headerTooltip]):ColDef<Inverter>=>({
      field,headerName,headerTooltip:`${headerTooltip} kWh/MWh/GWh otomatik seçilir; sayısal filtreler kWh üzerinden çalışır.`,
      width:155,minWidth:140,type:'numericColumn',cellDataType:'number',filter:'agNumberColumnFilter',
      cellClass:'ag-right-aligned-cell tabular-nums',valueFormatter:({value})=>formatEnergy(value),
    })),
    {
      field:'internal_temperature',headerName:'İç Sıcaklık (°C)',width:145,minWidth:130,
      type:'numericColumn',cellDataType:'number',filter:'agNumberColumnFilter',
      cellClass:'ag-right-aligned-cell tabular-nums',
      valueFormatter:({value})=>formatMetric(value,'°C'),
    },
    {
      field:'performance_ratio',headerName:'Performance Ratio (%)',width:180,minWidth:160,
      type:'numericColumn',cellDataType:'number',filter:'agNumberColumnFilter',
      cellClass:'ag-right-aligned-cell tabular-nums',
      valueFormatter:({value})=>formatMetric(value,'%'),
      headerTooltip:'İnverter kartlarıyla aynı gösterge: anlık gücün kurulu DC güce oranı. Işınıma göre düzeltilmiş performans oranı değildir.',
    },
    {
      field: 'last_seen',
      headerName: t('common.lastSeen'),
      flex: 1.5,
      minWidth: 160,
      filter: ColumnValuesFilter,
      filterValueGetter: ({ data }) => data?.last_seen ? formatLastSeen(data.last_seen, data.plant_timezone) : null,
      filterParams: { valueLabel: (value: unknown) => value ? String(value) : '—' },
      valueFormatter: ({ value, data }) => formatLastSeen(value, data?.plant_timezone),
      getQuickFilterText: ({ value, data }) => value ? formatLastSeen(value, data?.plant_timezone) : '',
      headerTooltip: 'Son ölçüm zamanı, santralin yerel saat diliminde gösterilir.',
      tooltipValueGetter: ({ value, data }) => value ? `${formatLastSeen(value, data?.plant_timezone)} · ${data?.plant_timezone ?? 'Europe/Istanbul'}` : '—',
    },
  ], [plantUuid, t]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
    suppressHeaderMenuButton: false,
    filter: true,
    floatingFilter: false,
  }), []);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {headerActionsTarget && createPortal(<div className="flex w-full min-w-0 flex-wrap items-center justify-start gap-1.5 xl:justify-end">
        <label className="flex items-center h-8 gap-1.5 rounded-lg border border-slate-200 px-2 text-slate-400"><Search size={14} /><input aria-label="İnverter listesinde ara" placeholder="İnverter, santral veya yatırımcı ara…" value={search} onChange={event => setSearch(event.target.value)} className="w-32 bg-transparent text-xs text-slate-700 outline-none xl:w-40" /></label>
        <div role="group" aria-label="İnverter durumu" className="flex h-8 items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5">{statusOptions.map(option => <button key={option.value} type="button" aria-pressed={statusFilter === option.value} title={option.value==='warning'?'Güncel Performance Ratio göstergesi %60 altında olan online inverterlar':undefined} onClick={() => setStatusFilter(option.value)} className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold transition ${statusFilter === option.value ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-white'}`}>{option.label}<span className={`rounded px-1 text-[10px] tabular-nums ${statusFilter === option.value ? 'bg-white/20' : 'bg-slate-200/60 text-slate-500'}`}>{option.count}</span></button>)}</div>
        <HomeTagFilter tags={availableTags} selectedIds={selectedTagIds} onChange={setSelectedTagIds} title="Bağlı santral etiketleri" allLabel="Tüm inverterlar göster" emptyLabel="Tüm inverterlar"/>

        <div role="status" title="Seçili filtrelerdeki inverterların güncel anlık güç toplamı" className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-amber-100 bg-amber-50 px-2.5 text-amber-800">
          <Zap size={13} className="shrink-0"/><span className="text-[10px] font-medium">Anlık Üretim</span><span className="text-xs font-bold tabular-nums">{loading || heatmapLoading?'…':formatMetric(dailySummary.power,'kW')}</span>
        </div>
        <div role="status" title={`Seçili filtrelerde bugüne ait sayaç kaydı: ${dailySummary.count}/${dailySummary.total} inverter. Eksik sayaçlar toplama katılmaz.`} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-sky-100 bg-sky-50 px-2.5 text-sky-800">
          <Zap size={13} className="shrink-0"/><span className="text-[10px] font-medium">Günlük Üretim</span><span className="text-xs font-bold tabular-nums">{loading || heatmapLoading?'…':formatEnergy(dailySummary.value)}</span>
        </div>
        <button type="button" onClick={toggleView} aria-label={cardView?'Tablo Görünümü':'Kart Görünümü'} title={cardView?'Tablo Görünümü':'Kart Görünümü'} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">{cardView?<Table2 size={14}/>:<LayoutGrid size={14}/>}</button>
      </div>,headerActionsTarget)}


      {!cardView ? <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="min-h-0 flex-1 [&_.ag-paging-row-summary-panel]:hidden">
        <AgGridReact<Inverter>
          modules={modules}
          theme={theme}

          rowData={gridRows}
          quickFilterText={search}
          getRowId={params => String(params.data.id)}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          loading={loading || heatmapLoading}
          animateRows
          suppressMovableColumns={false}
          enableCellTextSelection
          pagination
          paginationPageSize={100}
          paginationPageSizeSelector={[25, 50, 100]}
          tooltipShowDelay={250}
          overlayNoRowsTemplate="<span>Bu filtrelere uygun inverter bulunamadı.</span>"
        />
        </div>
      </section> : <div className="min-h-0 flex-1 overflow-y-auto pb-3 custom-scrollbar">
        <InverterHeatmapPanel
          initialMode="daily"
          showTitle={false}
          showSummary={false}
          rows={visibleHeatmapRows}
          loading={loading || heatmapLoading}
          onInverterClick={(inverterId) => navigate(`/dashboard/inverters/${inverterId}`)}
        />
      </div>}
    </div>
  );
}
